import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '../api/client.js';

/**
 * Documents on a case.
 *
 * The link role is chosen BEFORE the file, because the role is what the workflow
 * guards check. A file uploaded under the wrong role satisfies nothing and looks
 * like the system is broken, so the choice is deliberate rather than inferred
 * from a filename.
 */

interface CaseDocument {
  id: string;
  filename: string;
  mime_type: string;
  byte_size: number;
  scan_status: string;
  link_role: string;
  uploaded_by: string | null;
  created_at: string;
}

// The roles a dispatcher or clinician actually attaches by hand. The full
// vocabulary is larger; this is the everyday subset.
const COMMON_ROLES = [
  { key: 'bed_letter', label: 'Bed letter' },
  { key: 'signed_eprf', label: 'Signed ePRF' },
  { key: 'medical_report', label: 'Medical report' },
  { key: 'id_copy', label: 'ID copy' },
  { key: 'medical_aid_card', label: 'Medical aid card' },
  { key: 'proof_of_payment', label: 'Proof of payment' },
  { key: 'photo', label: 'Photo' },
  { key: 'other', label: 'Other' },
];

function fileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function DocumentPanel({ caseId }: { caseId: string }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [role, setRole] = useState('bed_letter');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['documents', caseId],
    queryFn: () => api.get<{ documents: CaseDocument[] }>(`/cases/${caseId}/documents`),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      // linkRole first: @fastify/multipart streams parts in order, and the
      // handler reads fields alongside the file.
      form.append('linkRole', role);
      form.append('file', file);
      const res = await fetch(`/api/v1/cases/${caseId}/documents`, {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
      });
      const body: unknown = await res.json();
      if (!res.ok) {
        const message =
          typeof body === 'object' && body !== null && 'error' in body
            ? String(body.error)
            : 'Upload failed';
        throw new Error(message);
      }
      return body;
    },
    onSuccess: async () => {
      setError(null);
      if (inputRef.current) inputRef.current.value = '';
      await qc.invalidateQueries({ queryKey: ['documents', caseId] });
      // Attaching a document can unblock a transition, so the guard evaluation
      // must be re-run — otherwise Approve QC stays greyed out until a refresh
      // and the system looks broken at the exact moment it started working.
      await qc.invalidateQueries({ queryKey: ['transitions', caseId] });
      await qc.invalidateQueries({ queryKey: ['case', caseId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const documents = data?.documents ?? [];

  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <h3 className="mb-3 text-xs font-semibold tracking-wider text-slate-400 uppercase">
        Documents
      </h3>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="rounded-lg border border-line bg-surface-3 px-2 py-1.5 text-sm text-slate-200"
        >
          {COMMON_ROLES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>

        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.txt"
          disabled={upload.isPending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
          }}
          className="text-sm text-slate-400 file:mr-2 file:rounded-lg file:border-0 file:bg-sky-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
        />
        {upload.isPending && <span className="text-xs text-slate-400">Uploading…</span>}
      </div>

      {error && <p className="mb-2 text-xs text-red-400">{error}</p>}

      {documents.length === 0 ? (
        <p className="text-xs text-slate-500">Nothing attached yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {documents.map((d) => (
            <li key={d.id} className="text-sm">
              {/* An audio document is a call recording. Rendered as a player
                  rather than a download link, because "listen to the call" is
                  the actual task — QC reviewing what the caller said, or a
                  clinician checking what was reported. Making that a download
                  to a temp folder is how it stops happening. */}
              {d.mime_type.startsWith('audio/') ? (
                <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-2">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="rounded bg-sky-600/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                      {d.link_role.replace(/_/g, ' ')}
                    </span>
                    <span className="truncate text-xs text-slate-400">{d.filename}</span>
                    <span className="ml-auto shrink-0 text-xs text-slate-500">
                      {fileSize(d.byte_size)}
                    </span>
                  </div>
                  {/* preload="none" deliberately: a case can carry several
                      recordings and a QC screen must not pull every one on
                      open. Opening a recording is also an access-logged read —
                      it should happen when someone chooses to listen. */}
                  <audio
                    controls
                    preload="none"
                    src={`/api/v1/documents/${d.id}/content`}
                    className="w-full"
                  >
                    Your browser cannot play audio.{' '}
                    <a href={`/api/v1/documents/${d.id}/content`}>Download the recording</a>.
                  </audio>
                </div>
              ) : (
                <RowLink d={d} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RowLink({ d }: { d: CaseDocument }) {
  return (
    <div className="flex items-center gap-2">
      <span className="rounded bg-slate-700/50 px-1.5 py-0.5 text-[10px] tracking-wide text-slate-300 uppercase">
        {d.link_role.replace(/_/g, ' ')}
      </span>
      <a
        href={`/api/v1/documents/${d.id}/content`}
        className="truncate text-slate-300 hover:text-sky-300 hover:underline"
      >
        {d.filename}
      </a>
      <span className="ml-auto shrink-0 text-xs text-slate-500">{fileSize(d.byte_size)}</span>
      {d.scan_status !== 'clean' && (
        <span className="shrink-0 rounded bg-amber-500/20 px-1.5 text-[10px] text-amber-300 uppercase">
          {d.scan_status}
        </span>
      )}
    </div>
  );
}
